import { ArrowRight, Check, CircleCheck, Clock, Lock, Mars, Venus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { ProfileForm } from '@escala/contracts';
import { AvailabilityGrid } from '@/components/AvailabilityGrid';
import { BrandMark, pageTitle } from '@/components/Brand';
import { AddressFields, ChurchFields, FormField, fieldErrorProps } from '@/components/MemberForm';
import OrgMark from '@/components/OrgMark';
import { LegalLink } from '@/components/Privacy';
import { cn } from '@/design-system/lib/cn';
import { Card } from '@/design-system/patterns/Card';
import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { CheckboxMark } from '@/design-system/ui/Checkbox';
import { DateField } from '@/design-system/ui/DateField';
import { Input } from '@/design-system/ui/Input';
import { Label } from '@/design-system/ui/Label';
import { SegmentedControl } from '@/design-system/ui/SegmentedControl';
import { SimpleSelect } from '@/design-system/ui/Select';
import { formatNumericDate, todayISO } from '@escala/domain/dates';
import { LEGAL_VERSION } from '@escala/domain/legal';
import {
  CIVIL_STATUS_LABEL,
  MAX_NICKNAME,
  cpfDigits,
  formatCpf,
  formatPhone,
  phoneDigits,
  toggleSlots,
} from '@escala/domain/members';
import { type SelfProfile, type SelfProfileErrors, selfProfileErrors } from '@escala/domain/profileLink';
import { CONSENT_TEXT } from '@escala/domain/registrations';
import type { CivilStatus } from '@escala/domain/types';

import { authErrorMessage } from './authError';
import NewPasswordForm from './components/NewPasswordForm';
import { Notice } from './PublicRegistration';

/** De onde o formulário lê e para onde salva (a API pública; nos testes, uma fonte falsa). */
export interface ProfileSource {
  load: () => Promise<ProfileForm>;
  save: (profile: SelfProfile) => Promise<void>;
  /** "Criar meu acesso" (só com a API): abre a sessão e encerra o link. */
  createAccount?: (email: string, password: string, termsVersion: string) => Promise<void>;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'missing' }
  | { kind: 'expired' }
  | { kind: 'failed' }
  | { kind: 'ready'; form: ProfileForm }
  | { kind: 'saved'; form: ProfileForm; profile: SelfProfile };

const STEPS = [
  'Seus dados',
  'Endereço e contato',
  'Vida na igreja',
  'Quando posso servir',
  'Revisão',
] as const;

const CIVIL_OPTIONS = [
  { value: 'none', label: 'Prefiro não informar', pin: true },
  ...(Object.entries(CIVIL_STATUS_LABEL) as [CivilStatus, string][]).map(([value, label]) => ({
    value,
    label,
  })),
];

const GRID = 'grid grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] gap-4';

/** Em que etapa está cada erro, para voltar a ela no "Salvar". */
function stepOf(errors: SelfProfileErrors): number {
  if (errors.name || errors.nickname || errors.sex || errors.phone || errors.email || errors.cpf) return 0;
  if (errors.cep || errors.uf || errors.emergencyPhone) return 1;

  return 2;
}

/** O que mostrar quando o formulário não abre: link trocado (404), vencido (410) ou falha. */
function failureOf(error: unknown): 'missing' | 'expired' | 'failed' {
  const status =
    error && typeof error === 'object' && 'status' in error ? (error as { status: number }).status : 0;

  if (status === 404) return 'missing';

  return status === 410 ? 'expired' : 'failed';
}

/**
 * O link pessoal para completar o cadastro (`/{igreja}/completar/{token}`): sem login, a
 * pessoa confere e completa os próprios dados em 5 etapas — o que já está guardado de
 * contato e privado não aparece (o link pode ter sido repassado), só a pista de que existe —
 * e salva com a autorização da LGPD; vale na hora. No fim, se quiser, cria o acesso ao
 * aplicativo.
 */
export default function CompleteProfile({
  source,
  closeLabel = 'Fechar',
  onClose,
  onSignedIn,
}: {
  source: ProfileSource;
  closeLabel?: string;
  onClose: () => void;
  /** Depois de "Criar meu acesso": a sessão já está aberta. */
  onSignedIn?: () => void;
}) {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [today] = useState(todayISO);

  useEffect(() => {
    document.title = pageTitle('Complete seu cadastro');

    let active = true;

    source
      .load()
      .then((form) => active && setPhase({ kind: 'ready', form }))
      .catch((error: unknown) => {
        if (!active) return;

        setPhase({ kind: failureOf(error) });
      });

    return () => {
      active = false;
    };
  }, [source]);

  // Quem rola é esta camada (fixa, com rolagem própria), não a janela.
  const scroller = useRef<HTMLDivElement>(null);
  const toTop = () => {
    if (scroller.current) scroller.current.scrollTop = 0;
  };
  const form = phase.kind === 'ready' || phase.kind === 'saved' ? phase.form : null;

  return (
    <div
      ref={scroller}
      role="dialog"
      aria-modal="true"
      aria-label="Complete seu cadastro"
      className="fixed inset-0 z-50 overflow-y-auto bg-page text-ink"
    >
      <div className="mx-auto flex max-w-170 flex-col gap-5 px-4 pt-7 pb-12">
        <header className="flex items-center gap-2.5">
          <BrandMark />
          <span className="flex-1" />
          {form ? (
            <span className="flex h-9.5 min-w-0 items-center gap-2 rounded-control border border-line-input bg-white pr-3 pl-1.5">
              <OrgMark org={form.org} size="sm" />
              <span className="max-w-55 truncate text-small font-semibold max-sm:max-w-30">
                {form.org.name}
              </span>
            </span>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            {closeLabel}
          </Button>
        </header>

        {phase.kind === 'loading' ? (
          <Card className="p-10 text-center text-ink-subtle" role="status">
            Abrindo o formulário…
          </Card>
        ) : null}
        {phase.kind === 'missing' ? (
          <Notice icon={<Lock className="size-6 stroke-[1.8]" />} tone="warning" title="Link inválido">
            Este link não existe mais — talvez a liderança tenha gerado um novo, ou você já criou o seu acesso
            (então entre pelo login). Peça o link atualizado.
          </Notice>
        ) : null}
        {phase.kind === 'expired' ? (
          <Notice icon={<Clock className="size-6 stroke-[1.8]" />} tone="warning" title="O link venceu">
            Os links para completar o cadastro valem 30 dias. Peça um novo à liderança do seu ministério.
          </Notice>
        ) : null}
        {phase.kind === 'failed' ? (
          <Notice
            icon={<Lock className="size-6 stroke-[1.8]" />}
            tone="warning"
            title="Não foi possível abrir"
          >
            Confira a internet e tente de novo em instantes.
          </Notice>
        ) : null}
        {phase.kind === 'ready' ? (
          <Steps
            form={phase.form}
            today={today}
            onStep={toTop}
            onSave={async (profile) => {
              await source.save(profile);
              setPhase({ kind: 'saved', form: phase.form, profile });
              toTop();
            }}
          />
        ) : null}
        {phase.kind === 'saved' ? (
          <Saved
            form={phase.form}
            profile={phase.profile}
            closeLabel={closeLabel}
            onClose={onClose}
            onEdit={() => {
              setPhase({ kind: 'ready', form: { ...phase.form, profile: phase.profile } });
              toTop();
            }}
            onCreate={
              source.createAccount
                ? async (email, password) => {
                    await source.createAccount!(email, password, LEGAL_VERSION);
                    onSignedIn?.();
                  }
                : undefined
            }
          />
        ) : null}
      </div>
    </div>
  );
}

/** As 5 etapas: cada "Continuar" confere a etapa; "Salvar" confere tudo e a LGPD. */
function Steps({
  form,
  today,
  onStep,
  onSave,
}: {
  form: ProfileForm;
  today: string;
  /** Leva ao topo a cada troca de etapa. */
  onStep: () => void;
  onSave: (profile: SelfProfile) => Promise<void>;
}) {
  const { known } = form;
  const [step, setStep] = useState(0);
  const [data, setData] = useState<SelfProfile>(form.profile);
  const [errors, setErrors] = useState<SelfProfileErrors>({});
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const firstName = form.profile.name.trim().split(/\s+/)[0] ?? '';

  const set = (patch: Partial<SelfProfile>) => {
    setData((d) => ({ ...d, ...patch }));
    setErrors({});
  };
  const go = (next: number) => {
    setStep(next);
    onStep();
  };

  const next = async () => {
    const found = selfProfileErrors(data, today);
    const here = Object.keys(found).length && stepOf(found) <= step;

    if (here || (step === STEPS.length - 1 && Object.keys(found).length)) {
      setErrors(found);
      go(stepOf(found));

      return;
    }

    if (step < STEPS.length - 1) {
      go(step + 1);

      return;
    }

    if (!consent) {
      setConsentError(true);

      return;
    }

    setBusy(true);
    setSendError(null);

    try {
      await onSave({ ...data, phone: phoneDigits(data.phone), cpf: cpfDigits(data.cpf) });
    } catch (error) {
      setSendError(authErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const keepHint = (has: boolean | string, what: string) =>
    has ? `Já temos ${typeof has === 'string' ? `o seu (${has})` : what}: preencha só se mudou.` : undefined;

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 text-display-lg leading-tight font-extrabold tracking-[-0.03em]">
          {firstName ? `Olá, ${firstName}!` : 'Complete seu cadastro'}
        </h1>
        <p className="m-0 text-ink-muted">
          Complete seus dados na {form.org.name} — leva uns 3 minutos e não precisa de senha. O link vale até{' '}
          {formatNumericDate(form.expiresAt.slice(0, 10))}.
        </p>
      </div>

      <ol aria-label="Etapas" className="m-0 flex list-none items-center gap-2 p-0">
        {STEPS.map((label, i) => (
          <li
            key={label}
            aria-current={i === step ? 'step' : undefined}
            className="flex min-w-0 flex-1 items-center gap-2"
          >
            <span
              className={cn(
                'grid size-7 shrink-0 place-items-center rounded-pill border-[1.5px] text-meta font-bold',
                i < step && 'border-primary bg-primary text-white',
                i === step && 'border-primary bg-primary-soft text-primary',
                i > step && 'border-line-input bg-white text-ink-subtle'
              )}
            >
              {i < step ? <Check aria-hidden className="size-3.5 stroke-3" /> : i + 1}
            </span>
            <span
              className={cn(
                'truncate text-meta font-semibold max-md:sr-only',
                i === step ? 'text-ink' : 'text-ink-subtle'
              )}
            >
              {label}
            </span>
          </li>
        ))}
      </ol>

      <Card className="flex flex-col gap-4.5 rounded-dialog p-6">
        <p className="m-0 text-meta font-semibold text-ink-subtle">
          Etapa {step + 1} de {STEPS.length} · {STEPS[step]}
        </p>

        {step === 0 ? (
          <div className={GRID}>
            <FormField id="cp-name" label="Nome completo" error={errors.name} className="col-span-full">
              <Input
                id="cp-name"
                autoComplete="name"
                value={data.name}
                onChange={(e) => set({ name: e.target.value })}
                {...fieldErrorProps('cp-name', errors.name)}
              />
            </FormField>
            <FormField id="cp-nickname" label="Como prefere ser chamado(a)" optional error={errors.nickname}>
              <Input
                id="cp-nickname"
                placeholder="O nome do crachá"
                maxLength={MAX_NICKNAME}
                value={data.nickname}
                onChange={(e) => set({ nickname: e.target.value })}
              />
            </FormField>
            <FormField id="cp-birth" label="Data de nascimento" optional>
              <DateField
                id="cp-birth"
                autoComplete="bday"
                max={today}
                value={data.birthDate ?? ''}
                onChange={(iso) => set({ birthDate: iso || null })}
              />
            </FormField>
            <div className="flex flex-col gap-1.5">
              <span className="text-small font-semibold">Sexo</span>
              <SegmentedControl
                aria-label="Sexo"
                block
                value={data.sex}
                onValueChange={(sex) => set({ sex })}
                options={[
                  {
                    value: 'F',
                    label: (
                      <>
                        <Venus aria-hidden />
                        Feminino
                      </>
                    ),
                  },
                  {
                    value: 'M',
                    label: (
                      <>
                        <Mars aria-hidden />
                        Masculino
                      </>
                    ),
                  },
                ]}
              />
              {errors.sex ? (
                <span role="alert" className="text-caption text-status-conflict">
                  {errors.sex}
                </span>
              ) : null}
            </div>
            <FormField id="cp-civil" label="Estado civil" optional>
              <SimpleSelect
                id="cp-civil"
                aria-label="Estado civil"
                value={data.civilStatus ?? 'none'}
                onValueChange={(v) => set({ civilStatus: v === 'none' ? null : (v as CivilStatus) })}
                options={CIVIL_OPTIONS}
              />
            </FormField>
            <FormField id="cp-job" label="Profissão" optional>
              <Input
                id="cp-job"
                value={data.profession}
                onChange={(e) => set({ profession: e.target.value })}
              />
            </FormField>
            <FormField
              id="cp-phone"
              label="Celular (WhatsApp)"
              optional
              error={errors.phone}
              hint={keepHint(known.phone, 'o telefone')}
            >
              <Input
                id="cp-phone"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                data-numeric
                placeholder="(84) 91234-5678"
                value={formatPhone(data.phone)}
                onChange={(e) => set({ phone: phoneDigits(e.target.value) })}
                {...fieldErrorProps('cp-phone', errors.phone)}
              />
            </FormField>
            <FormField
              id="cp-email"
              label="E-mail"
              optional
              error={errors.email}
              hint={keepHint(known.email, 'o e-mail')}
            >
              <Input
                id="cp-email"
                type="email"
                autoComplete="email"
                placeholder="nome@exemplo.com"
                value={data.email}
                onChange={(e) => set({ email: e.target.value })}
                {...fieldErrorProps('cp-email', errors.email)}
              />
            </FormField>
            <FormField
              id="cp-cpf"
              label="CPF"
              optional
              error={errors.cpf}
              hint={keepHint(known.cpf, 'o seu CPF')}
            >
              <Input
                id="cp-cpf"
                inputMode="numeric"
                data-numeric
                placeholder="000.000.000-00"
                value={formatCpf(data.cpf)}
                onChange={(e) => set({ cpf: cpfDigits(e.target.value).slice(0, 11) })}
                {...fieldErrorProps('cp-cpf', errors.cpf)}
              />
            </FormField>
          </div>
        ) : null}

        {step === 1 ? (
          <div className="flex flex-col gap-4.5">
            {known.address ? (
              <Alert tone="info">
                Já temos o seu endereço. Preencha só se mudou — em branco, fica como está.
              </Alert>
            ) : null}
            <div className={GRID}>
              <AddressFields
                idPrefix="cp-address"
                address={data.address}
                optional
                errors={errors}
                onChange={(address) => set({ address })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="m-0 text-control font-bold">Contato de emergência</h2>
              {known.emergency ? (
                <p className="m-0 text-meta text-ink-subtle">Já temos um contato. Preencha só se mudou.</p>
              ) : null}
            </div>
            <div className={GRID}>
              <FormField id="cp-sos-name" label="Nome do contato" optional>
                <Input
                  id="cp-sos-name"
                  value={data.emergency.name}
                  onChange={(e) => set({ emergency: { ...data.emergency, name: e.target.value } })}
                />
              </FormField>
              <FormField id="cp-sos-relation" label="Parentesco" optional>
                <Input
                  id="cp-sos-relation"
                  placeholder="Mãe, cônjuge…"
                  value={data.emergency.relation}
                  onChange={(e) => set({ emergency: { ...data.emergency, relation: e.target.value } })}
                />
              </FormField>
              <FormField id="cp-sos-phone" label="Telefone do contato" optional error={errors.emergencyPhone}>
                <Input
                  id="cp-sos-phone"
                  type="tel"
                  inputMode="numeric"
                  data-numeric
                  value={formatPhone(data.emergency.phone)}
                  onChange={(e) =>
                    set({ emergency: { ...data.emergency, phone: phoneDigits(e.target.value) } })
                  }
                  {...fieldErrorProps('cp-sos-phone', errors.emergencyPhone)}
                />
              </FormField>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className={GRID}>
            <ChurchFields
              idPrefix="cp-church"
              value={data}
              today={today}
              optional
              errors={errors}
              onChange={set}
            />
          </div>
        ) : null}

        {step === 3 ? (
          <div className="flex flex-col gap-3">
            <p className="m-0 text-small text-ink-muted">
              Marque os períodos em que você pode servir. Toque no dia ou no período para marcar a linha ou a
              coluna inteira. Datas específicas (férias, viagens) você avisa depois, no app ou à liderança.
            </p>
            <AvailabilityGrid
              value={data.availability}
              editable
              onSet={(indexes, value) =>
                set({ availability: toggleSlots(data.availability, indexes, value) })
              }
            />
          </div>
        ) : null}

        {step === 4 ? (
          <Review
            data={data}
            orgName={form.org.name}
            consent={consent}
            consentError={consentError}
            onEdit={go}
            onConsent={(v) => {
              setConsent(v);
              if (v) setConsentError(false);
            }}
          />
        ) : null}

        {sendError ? <Alert tone="danger">{sendError}</Alert> : null}

        <div className="flex items-center gap-2 border-t border-line-divider pt-4">
          {step > 0 ? (
            <Button variant="secondary" size="lg" onClick={() => go(step - 1)}>
              Voltar
            </Button>
          ) : null}
          <span className="flex-1" />
          <Button size="lg" className="font-bold shadow-cta" disabled={busy} onClick={() => void next()}>
            {step === STEPS.length - 1 ? 'Salvar meu cadastro' : 'Continuar'}
            <ArrowRight aria-hidden />
          </Button>
        </div>
      </Card>
      <p className="m-0 text-center text-caption text-ink-subtle">
        Seus dados ficam com a secretaria e a liderança da igreja. CPF, endereço e contatos já informados não
        aparecem aqui, por segurança.
      </p>
    </>
  );
}

function Review({
  data,
  orgName,
  consent,
  consentError,
  onConsent,
  onEdit,
}: {
  data: SelfProfile;
  orgName: string;
  consent: boolean;
  consentError: boolean;
  onConsent: (value: boolean) => void;
  onEdit: (step: number) => void;
}) {
  const kept = 'Fica como está';
  const available = data.availability.filter((v) => v === 'available').length;
  const rows: [string, string, number][] = [
    ['Nome', data.name, 0],
    ['Como prefere ser chamado(a)', data.nickname || '—', 0],
    ['Nascimento', data.birthDate ? formatNumericDate(data.birthDate) : '—', 0],
    ['Celular', data.phone ? formatPhone(data.phone) : kept, 0],
    ['E-mail', data.email || kept, 0],
    ['Endereço', data.address.street || data.address.city || data.address.cep ? 'Novo endereço' : kept, 1],
    ['Disponibilidade', available ? `${available} de 21 períodos` : 'Não informada', 3],
  ];

  return (
    <div className="flex flex-col gap-3">
      <dl className="m-0 grid grid-cols-[repeat(auto-fit,minmax(min(100%,190px),1fr))] gap-x-4 gap-y-2.5 rounded-card border border-line-card px-3.5 py-3">
        {rows.map(([label, value, step]) => (
          <div key={label} className="min-w-0">
            <dt className="text-caption font-semibold text-ink-subtle">{label}</dt>
            <dd className="m-0 flex items-baseline gap-2 text-control wrap-anywhere">
              {value}
              <Button
                variant="link"
                size="sm"
                aria-label={`Editar ${label.toLowerCase()}`}
                onClick={() => onEdit(step)}
              >
                Editar
              </Button>
            </dd>
          </div>
        ))}
      </dl>
      <button
        type="button"
        role="checkbox"
        aria-checked={consent}
        aria-describedby={consentError ? 'cp-consent-error' : undefined}
        onClick={() => onConsent(!consent)}
        className={cn(
          'flex items-start gap-2.5 rounded-block border bg-white px-3.5 py-3 text-left',
          consentError ? 'border-status-conflict' : 'border-line-input'
        )}
      >
        <CheckboxMark checked={consent} className="mt-0.5" />
        <span className="text-small text-pretty text-ink-secondary">{CONSENT_TEXT}</span>
      </button>
      <p className="m-0 -mt-1 text-meta text-ink-subtle">
        Leia a{' '}
        <LegalLink doc="privacy" orgName={orgName}>
          Política de Privacidade
        </LegalLink>{' '}
        e os{' '}
        <LegalLink doc="terms" orgName={orgName}>
          Termos de Uso
        </LegalLink>
        .
      </p>
      {consentError ? (
        <span id="cp-consent-error" role="alert" className="text-caption text-status-conflict">
          Para salvar, autorize o uso dos dados.
        </span>
      ) : null}
    </div>
  );
}

/** Salvo: a confirmação e o convite opcional para criar o acesso ao aplicativo. */
function Saved({
  form,
  profile,
  closeLabel,
  onClose,
  onEdit,
  onCreate,
}: {
  form: ProfileForm;
  profile: SelfProfile;
  closeLabel: string;
  onClose: () => void;
  onEdit: () => void;
  onCreate?: (email: string, password: string) => Promise<void>;
}) {
  const [creating, setCreating] = useState(false);
  const [email, setEmail] = useState(profile.email);
  const [terms, setTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const firstName = profile.name.trim().split(/\s+/)[0] ?? '';

  return (
    <>
      <Notice
        icon={<CircleCheck className="size-7 stroke-[1.8]" />}
        tone="success"
        title={`Cadastro salvo, ${firstName}!`}
        actions={
          <>
            <Button variant="secondary" size="lg" onClick={onEdit}>
              Corrigir algo
            </Button>
            {!creating ? (
              <Button size="lg" variant={onCreate ? 'secondary' : 'primary'} onClick={onClose}>
                {onCreate ? 'Agora não' : closeLabel}
              </Button>
            ) : null}
          </>
        }
      >
        Obrigado! A liderança da {form.org.name} já vê os seus dados. O link continua valendo até{' '}
        {formatNumericDate(form.expiresAt.slice(0, 10))}, se precisar corrigir.
      </Notice>
      {onCreate ? (
        <Card className="flex flex-col gap-4 rounded-dialog p-6">
          <div>
            <h2 className="m-0 text-lead font-bold">Quer acessar o Live Gospel?</h2>
            <p className="m-0 text-small text-ink-muted">
              Opcional. Com o acesso você vê suas escalas, confirma presença e avisa quando não puder ir.
            </p>
          </div>
          {creating ? (
            <NewPasswordForm
              before={
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="cp-login-email">E-mail (o do login)</Label>
                  <Input
                    id="cp-login-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                  />
                </div>
              }
              action={
                <>
                  Criar meu acesso
                  <ArrowRight aria-hidden />
                </>
              }
              after={
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={terms}
                    onClick={() => {
                      setTerms(!terms);
                      setTermsError(false);
                    }}
                    className={cn(
                      'flex items-start gap-2.5 rounded-block border bg-white px-3.5 py-3 text-left',
                      termsError ? 'border-status-conflict' : 'border-line-input'
                    )}
                  >
                    <CheckboxMark checked={terms} className="mt-0.5" />
                    <span className="text-small text-pretty text-ink-secondary">
                      Li e aceito os Termos de Uso e a Política de Privacidade.
                    </span>
                  </button>
                  {termsError ? (
                    <span role="alert" className="text-caption text-status-conflict">
                      Para criar o acesso, aceite os Termos e a Política de Privacidade.
                    </span>
                  ) : null}
                </div>
              }
              guard={() => {
                setTermsError(!terms);

                return terms && !!email.trim();
              }}
              onSubmit={(password) => onCreate(email.trim(), password)}
            />
          ) : (
            <Button size="lg" onClick={() => setCreating(true)}>
              Criar meu acesso
            </Button>
          )}
        </Card>
      ) : null}
    </>
  );
}
